import IconCheck from "@/src/assets/icons/icon-check-shape.svg";
import CompletedIcon from "@/src/assets/icons/icon-completed.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import CallOut from "@/src/components/molecule/CallOut";
import SectionCardWithoutHeader from "@/src/components/molecule/SectionCardWithoutHeader";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import {
  getFormattedTimezoneDateWithOffset,
  getTimezoneDateWithOffset,
} from "@/src/hooks/useCompanyLocalDate";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import colorSet from "@/src/styles/color";
import { addZeroPrefix } from "@/src/utils/addZeroPrefix";
import { isDevelopment } from "@/src/utils/environment";
import { authApi, useEditExporterUserAdditionalInfosMutation } from "@/src/store/apis/auth";
import { isUndefined } from "@/src/utils/is";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useEnterPriseSignupDateContext } from "../hooks";

function EnterpriseSignupCompleted() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionStartAtFrom, subscriptionEndAtTo, planName } =
    useEnterPriseSignupDateContext();

  // const { data: subscription } = useGetCurrentSubscriptionQuery();
  const [editExporterUserAdditionalInfos] = useEditExporterUserAdditionalInfosMutation();

  // Get gmtOffset from session
  const { gmtOffset } = authApi.endpoints.getSession.useQueryState(undefined, {
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        gmtOffset: isStable ? currentData.row.exporter.gmtOffset || 0 : 0,
      };
    },
  });

  const goToHome = async () => {
    try {
      await editExporterUserAdditionalInfos({
        isInitialGuideNeeded: false,
      });
      navigate(EXPORTER_PRIVATE_PATH.SUBSCRIPTION_MANAGEMENT, { replace: true });
    } catch (error) {
      if (isDevelopment) {
        console.error("첫 로그인 완료 처리 실패:", error);
      }
      navigate(EXPORTER_PRIVATE_PATH.SUBSCRIPTION_MANAGEMENT, { replace: true });
    }
  };

  const getSubscriptionDate = () => {
    // Handle undefined dates
    if (!subscriptionStartAtFrom || !subscriptionEndAtTo) {
      return {
        startDate: "-",
        endDate: "-",
        nextPaymentYear: 0,
        nextPaymentMonth: "00",
        nextPaymentDay: "00",
      };
    }

    // Use timezone-aware dates
    const localEndDate = getTimezoneDateWithOffset({
      date: subscriptionEndAtTo,
      gmtOffset,
    });
    const nextPayment = localEndDate.add(1, "day");
    const nextYear = nextPayment.get("year");
    const nextMonth = addZeroPrefix(nextPayment.get("month") + 1);
    const nextDate = addZeroPrefix(nextPayment.get("date"));

    return {
      startDate: getFormattedTimezoneDateWithOffset({
        date: subscriptionStartAtFrom,
        format: DATE_FORMAT_STRINGS.YYYY_MM_DD,
        gmtOffset,
      }),
      endDate: getFormattedTimezoneDateWithOffset({
        date: subscriptionEndAtTo,
        format: DATE_FORMAT_STRINGS.YYYY_MM_DD,
        gmtOffset,
      }),
      nextPaymentYear: nextYear,
      nextPaymentMonth: nextMonth,
      nextPaymentDay: nextDate,
    };
  };

  return (
    <StyledSectionCard>
      <FlexColumn gap={40}>
        {/* Information */}
        <PlanInfo>
          <FlexColumn alignItems="center">
            <Icon iconSrc={CompletedIcon} iconSize={60} />
          </FlexColumn>

          <FlexColumn gap={12} alignItems="center">
            <Typo typoType="d6">
              {t("enterpriseSignup:planSubscriptionCompleted")}
            </Typo>
            <Typo>
              {t("enterpriseSignup:planSubscriptionCompletedDescription")}
            </Typo>
          </FlexColumn>

          <StyledCallout backgroundColor="white">
            <FlexColumn gap={16}>
              <FlexColumn gap={4}>
                <Typo typoType="h4">{planName ?? "-"}</Typo>

                <FlexRow gap={4}>
                  <Typo typoType="b7m">
                    {getSubscriptionDate().startDate}–
                    {getSubscriptionDate().endDate}
                  </Typo>
                </FlexRow>
              </FlexColumn>

              <Divider />

              <FlexRow gap={4}>
                <Icon iconSrc={IconCheck} iconSize={16} />
                {i18n.language === "ko" ? (
                  <Typo>
                    {getSubscriptionDate().nextPaymentYear}
                    {t("enterpriseSignup:year")}{" "}
                    {getSubscriptionDate().nextPaymentMonth}
                    {t("enterpriseSignup:month")}{" "}
                    {getSubscriptionDate().nextPaymentDay}
                    {t("enterpriseSignup:automaticPaymentDescription")}
                  </Typo>
                ) : (
                  <Typo>
                    {t("enterpriseSignup:automaticPaymentWillCommencefrom")}{" "}
                    {subscriptionEndAtTo
                      ? getTimezoneDateWithOffset({
                          date: subscriptionEndAtTo,
                          gmtOffset,
                        })
                          .add(1, "day")
                          .format(DATE_FORMAT_STRINGS.YYYY_MM_DD)
                      : "-"}
                  </Typo>
                )}
              </FlexRow>
            </FlexColumn>
          </StyledCallout>
        </PlanInfo>

        {/* Button */}
        <StyledButton
          buttonColor="blue"
          buttonGrade="secondary"
          onClick={goToHome}
        >
          {t("enterpriseSignup:button.home")}
        </StyledButton>
      </FlexColumn>
    </StyledSectionCard>
  );
}

export default EnterpriseSignupCompleted;

const StyledSectionCard = styled(SectionCardWithoutHeader)`
  width: 640px;
  padding: 40px 48px;
`;

const FlexColumn = styled.div<{
  gap?: number;
  alignItems?: CSSProperties["alignItems"];
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap}px;
  align-items: ${({ alignItems }) => alignItems};
  width: 100%;
`;

const FlexRow = styled(FlexColumn)`
  flex-direction: row;
  align-items: center;
`;

const PlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px 32px 0;
  gap: 40px;
`;

const StyledButton = styled(Button)`
  text-align: center;
`;

const StyledCallout = styled(CallOut)``;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${colorSet.gray9};
`;
